import React from 'react';
import { Link } from 'react-router-dom';
import Seo from '../components/Seo';
import ReviewsSection from '../components/ReviewsSection';
import JobGallery from '../components/JobGallery';
import { motion } from 'framer-motion';
import { Phone, ArrowRight, Clock, Droplets, Flame, Home, ShieldCheck, Wrench } from 'lucide-react';
import Hero from '../components/Hero';
import TrustBadges from '../components/TrustBadges';
import ServiceCards from '../components/ServiceCards';
import AlsoServing from '../components/AlsoServing';
import OwnerSection from '../components/OwnerSection';
import ServiceArea from '../components/ServiceArea';

const PHONE = "587-355-FLOW (3569)";

// Common Problems Section
const CommonProblems = () => {
  const problems = [
    { icon: Flame, title: 'Water Heater Not Working', link: '/plumbing/water-heater-replacement-calgary', desc: 'Expert water heater repair & replacement' },
    { icon: Droplets, title: 'Poly-B Plumbing Replacement', link: '/plumbing/poly-b-replacement-calgary', desc: 'Upgrade to reliable PEX plumbing' },
    { icon: Home, title: 'Basement Plumbing for Secondary Suites', link: '/plumbing/secondary-suite-plumbing-calgary', desc: 'Code-compliant suite plumbing' },
    { icon: ShieldCheck, title: 'Backflow Preventer Testing', link: '/plumbing/backflow-testing-calgary', desc: 'City of Calgary certified testing' },
    { icon: Wrench, title: 'In-Floor Heating Installation', link: '/heating/in-floor-radiant-heating-calgary', desc: 'Radiant floor heating systems' },
    { icon: Flame, title: 'Boiler or Hydronic Heating Problems', link: '/heating/boiler-hydronic-calgary', desc: 'Hydronic system service & repair' },
  ];

  return (
    <section data-testid="common-problems-section" className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 1, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <span className="text-[#1E3A5F] uppercase tracking-widest text-sm font-semibold">How We Can Help</span>
          <h2 className="font-heading font-black text-3xl sm:text-4xl text-gray-900 mt-3 mb-4">
            Common Problems We Fix
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            From repairs to planned upgrades, we handle all your plumbing and mechanical needs.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {problems.map((problem, index) => (
            <motion.div
              key={problem.title}
              initial={{ opacity: 1, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
            >
              <Link
                to={problem.link}
                className="block p-6 bg-gray-50 rounded-xl border border-gray-100 hover:border-[#1E3A5F]/30 hover:shadow-lg transition-all group"
              >
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-[#1E3A5F]/10 rounded-xl">
                    <problem.icon className="w-6 h-6 text-[#1E3A5F]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-lg text-gray-900 mb-1 group-hover:text-[#1E3A5F] transition-colors">
                      {problem.title}
                    </h3>
                    <p className="text-gray-500 text-sm">{problem.desc}</p>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Fast Service Banner
const FastServiceBanner = () => {
  return (
    <section data-testid="fast-service-banner" className="py-12 bg-gradient-to-r from-[#1E3A5F] to-[#152A45]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4 text-white">
            <div className="p-3 bg-white/20 rounded-full">
              <Clock className="w-8 h-8" />
            </div>
            <div>
              <h3 className="font-heading font-bold text-2xl">Fast Service Across Calgary</h3>
              <p className="text-white/90">Fast, reliable service for many plumbing and mechanical issues.</p>
            </div>
          </div>
          <a
            href={`tel:+15873553569`}
            className="bg-white text-[#1E3A5F] font-bold uppercase tracking-wider px-8 py-4 rounded-lg hover:bg-gray-100 transition-colors flex items-center gap-2 whitespace-nowrap"
          >
            <Phone className="w-5 h-5" />
            Call Now
          </a>
        </div>
      </div>
    </section>
  );
};

// Why Choose Section
const WhyChoose = () => {
  const reasons = [
    { title: 'Licensed & Insured', desc: 'Licensed in Calgary, for your peace of mind.' },
    { title: 'Upfront Pricing', desc: 'Know the cost before we start. No hidden fees or surprises.' },
    { title: 'Quality Workmanship', desc: 'Every job done right the first time with premium materials.' },
    { title: 'Local Experts', desc: 'Calgary-based team who knows your community.' },
  ];

  return (
    <section data-testid="why-choose-section" className="py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 1, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <span className="text-[#1E3A5F] uppercase tracking-widest text-sm font-semibold">Why Choose Us</span>
          <h2 className="font-heading font-black text-3xl sm:text-4xl text-gray-900 mt-3 mb-4">
            Why Choose Flow Forge Mechanical
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {reasons.map((reason, index) => (
            <motion.div
              key={reason.title}
              initial={{ opacity: 1, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm text-center"
            >
              <h3 className="font-heading font-bold text-lg text-gray-900 mb-2">{reason.title}</h3>
              <p className="text-gray-600 text-sm">{reason.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Electrical Partner Preview
const ElectricalPartnerPreview = () => {
  return (
    <section data-testid="electrical-partner-preview" className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 lg:p-12 border border-gray-200">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <span className="text-[#1E3A5F] uppercase tracking-widest text-sm font-semibold">Trusted Partner</span>
              <h2 className="font-heading font-black text-2xl sm:text-3xl text-gray-900 mt-3 mb-4">
                Electrical Services Partner
              </h2>
              <p className="text-gray-600 mb-4">
                Electrical work required for mechanical systems is handled through our trusted partner, 
                <strong> Brandon Marsh</strong> - Master Electrician and owner of YYC Electrical Solutions.
              </p>
              <Link
                to="/about"
                className="inline-flex items-center gap-2 text-[#1E3A5F] font-semibold hover:gap-3 transition-all"
              >
                Learn More
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
            <div className="flex justify-center lg:justify-end">
              <div className="p-6 bg-white rounded-2xl shadow-lg border border-gray-100">
                <img 
                  src="/yyc-electrical-logo.png" 
                  alt="YYC Electrical Solutions" 
                  className="h-24 w-auto mx-auto"
                />
                <p className="text-center mt-4 text-gray-700 font-medium">Master Electrician Services</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Final CTA Section
const FinalCTA = () => {
  return (
    <section data-testid="final-cta" className="py-16 cta-banner">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="font-heading font-bold text-2xl md:text-3xl text-white mb-4">
          Ready to Request Service?
        </h2>
        <p className="text-white/90 mb-6 max-w-xl mx-auto">
          Contact us today for fast, reliable plumbing, heating, air conditioning, and gas fitting services in Calgary.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link
            to="/contact"
            className="bg-white text-[#1E3A5F] font-bold uppercase tracking-wider px-8 py-4 rounded-lg hover:bg-gray-100 transition-colors inline-flex items-center justify-center gap-2"
          >
            Request Service
            <ArrowRight className="w-5 h-5" />
          </Link>
          <a
            href={`tel:+15873553569`}
            className="border-2 border-white text-white font-bold uppercase tracking-wider px-8 py-4 rounded-lg hover:bg-white/10 transition-colors inline-flex items-center justify-center gap-2"
          >
            <Phone className="w-5 h-5" />
            Call Now
          </a>
        </div>
      </div>
    </section>
  );
};

export const HomePage = () => {
  return (
    <main data-testid="home-page" className="page-transition">
      <Seo title="Calgary Plumber, Heating & Gas Fitter | Flow Forge Mechanical" description="Plumbing, heating and gas fitting by our licensed, insured team in Calgary and Surrounding Areas. Furnaces, water heaters, gas lines. Call 587-355-3569." path="/" preloadImage="/images/flowforge/basement-roughin-1.jpg" />
      <Hero />
      <FastServiceBanner />
      <TrustBadges />
      <CommonProblems />
      <WhyChoose />
      <ServiceCards />
      <OwnerSection />
      <ElectricalPartnerPreview />
      <ServiceArea />
      <ReviewsSection location="home" />
      <JobGallery />
      <AlsoServing />
      <FinalCTA />
    </main>
  );
};

export default HomePage;
